import { useState } from 'react';
import { Box, Button, Flex, Input, Stack, Text } from '@chakra-ui/react';
import { Check, PencilSimpleLine, X } from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import { apiUpdateJobCandidateScore } from '@/api/services';
import CircleProgressBar from '@/components/data-display/CircularProgressBar';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { CandidateWithJobContext, CandidateUserScore } from '@/types/Candidate';
import { JobCandidateStatus } from '@/types/enums';

const MIN_SCORE = 0;
const MAX_SCORE = 100;

export default function ScoreDetailsDialog({
  jobId,
  candidate,
  onUpdateScore
}: {
  jobId: string;
  candidate: CandidateWithJobContext;
  onUpdateScore: (userScore: CandidateUserScore) => void;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [editResumeScore, setEditResumeScore] = useState<boolean>(false);
  const [resumeScoreLoading, setResumeScoreLoading] = useState<boolean>(false);
  const [resumeScoreError, setResumeScoreError] = useState<string | null>(null);
  const [editInterviewScore, setEditInterviewScore] = useState<boolean>(false);
  const [interviewScoreLoading, setInterviewScoreLoading] = useState<boolean>(false);
  const [interviewScoreError, setInterviewScoreError] = useState<string | null>(null);
  const [resumeScore, setResumeScore] = useState<number>(candidate.user_score.resume_score || 0);
  const [interviewScore, setInterviewScore] = useState<number>(
    candidate.user_score.interview_score || 0
  );

  const validateScore = (score: number): string | null => {
    if (score < MIN_SCORE || score > MAX_SCORE) {
      return `Score must be between ${MIN_SCORE} and ${MAX_SCORE}`;
    }
    return null;
  };

  const updateScore = async (type: 'resume' | 'interview') => {
    const score = type === 'resume' ? resumeScore : interviewScore;
    setSubmitError(false);
    setResumeScoreError(null);
    setInterviewScoreError(null);

    const error = validateScore(score);
    if (error) {
      if (type === 'resume') {
        setResumeScoreError(error);
      } else {
        setInterviewScoreError(error);
      }
      return;
    }

    if (type === 'resume') {
      setResumeScoreLoading(true);
    } else {
      setInterviewScoreLoading(true);
    }

    try {
      const response = await fetchAPI<CandidateUserScore>({
        request: () =>
          apiUpdateJobCandidateScore({
            jobId,
            candidateId: candidate.candidate_id,
            payload: { type, score }
          })
      });

      if (response.success) {
        onUpdateScore(response.data);
        if (type === 'resume') {
          setEditResumeScore(false);
        } else {
          setEditInterviewScore(false);
        }
      } else {
        console.error('Failed to update candidate score:', response.message);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Failed to update candidate score:', error);
      setSubmitError(true);
    } finally {
      if (type === 'resume') {
        setResumeScoreLoading(false);
      } else {
        setInterviewScoreLoading(false);
      }
    }
  };

  const handleDialogClose = () => {
    setSubmitError(false);
    setIsDialogOpen(false);
    setEditResumeScore(false);
    setEditInterviewScore(false);
    setResumeScore(candidate.user_score.resume_score || 0);
    setInterviewScore(candidate.user_score.interview_score || 0);
  };

  return (
    <DialogRoot
      open={isDialogOpen}
      onOpenChange={({ open }) => {
        setIsDialogOpen(open);
      }}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      unmountOnExit
      size="lg"
    >
      <DialogTrigger asChild>
        <Button
          bg="background.secondary"
          border="1px solid"
          borderColor="primary.900"
          borderRadius="8px"
          color="primary.900"
          fontSize="12px"
          px="8px"
          disabled={candidate.status === JobCandidateStatus.New}
          _hover={{
            bg: 'primary.50'
          }}
        >
          See Score Details
        </Button>
      </DialogTrigger>
      <DialogContent borderRadius="12px" p="12px 24px">
        <DialogHeader>
          <DialogTitle>Score Details</DialogTitle>
        </DialogHeader>
        <DialogBody alignItems="center" justifyContent="center">
          <Stack gap="24px">
            <Flex justifyContent="space-between" w="100%">
              <Box flex={1} display="flex" alignItems="center" justifyContent="center" />
              <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
                  System
                </Text>
              </Box>
              <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
                  User
                </Text>
              </Box>
              <Box flex={1} display="flex" alignItems="center" justifyContent="center" />
            </Flex>
            <Flex justifyContent="space-between" w="100%">
              <Box flex={1} display="flex" alignItems="center">
                <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
                  Resume
                </Text>
              </Box>
              <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="16px" color="neutrals.900">
                  {candidate.resume_analysis_result.fit_score.score}
                </Text>
              </Box>
              <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                {editResumeScore ? (
                  <Input
                    w="70px"
                    fontSize="16px"
                    placeholder="Enter score"
                    type="number"
                    border="1px solid"
                    borderColor={resumeScoreError ? 'error.500' : 'neutrals.300'}
                    borderRadius="8px"
                    textAlign="center"
                    disabled={resumeScoreLoading}
                    _focus={{
                      borderColor: 'primary.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)'
                    }}
                    value={resumeScore}
                    onChange={(e) => setResumeScore(Number(e.target.value))}
                  />
                ) : (
                  <Text fontSize="16px" color="neutrals.900">
                    {candidate.user_score.resume_score === null
                      ? 'N/A'
                      : candidate.user_score.resume_score}
                  </Text>
                )}
              </Box>
              <Box flex={1} display="flex" alignItems="center" justifyContent="flex-end">
                {editResumeScore ? (
                  <Flex>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditResumeScore(false);
                        setResumeScore(candidate.user_score.resume_score || 0);
                      }}
                      color="primary.900"
                      disabled={resumeScoreLoading}
                      p="0"
                    >
                      <X size={24} weight="bold" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => updateScore('resume')}
                      color="primary.900"
                      loading={resumeScoreLoading}
                      disabled={interviewScoreLoading}
                      p="0"
                    >
                      <Check size={24} weight="bold" />
                    </Button>
                  </Flex>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => setEditResumeScore(true)}
                    color="primary.900"
                    p="0"
                  >
                    <PencilSimpleLine size={24} weight="bold" />
                  </Button>
                )}
              </Box>
            </Flex>
            {(candidate.interview_analysis_result || candidate.screening_analysis_result) && (
              <>
                {' '}
                <Flex justifyContent="space-between" w="100%">
                  <Box flex={1} display="flex" alignItems="center">
                    <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
                      Interview
                    </Text>
                  </Box>
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                    <Text fontSize="16px" color="neutrals.900">
                      {candidate.interview_analysis_result
                        ? candidate.interview_analysis_result.score.score
                        : candidate.screening_analysis_result?.score.score || 'N/A'}
                    </Text>
                  </Box>
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                    {editInterviewScore ? (
                      <Input
                        w="70px"
                        fontSize="16px"
                        placeholder="Enter score"
                        type="number"
                        border="1px solid"
                        borderColor={interviewScoreError ? 'error.500' : 'neutrals.300'}
                        borderRadius="8px"
                        textAlign="center"
                        _focus={{
                          borderColor: 'primary.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)'
                        }}
                        value={interviewScore}
                        onChange={(e) => setInterviewScore(Number(e.target.value))}
                        disabled={interviewScoreLoading}
                      />
                    ) : (
                      <Text fontSize="16px" color="neutrals.900">
                        {candidate.user_score.interview_score === null
                          ? 'N/A'
                          : candidate.user_score.interview_score}
                      </Text>
                    )}
                  </Box>
                  <Box flex={1} display="flex" alignItems="center" justifyContent="flex-end">
                    {editInterviewScore ? (
                      <Flex>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setInterviewScore(candidate.user_score.interview_score || 0);
                            setEditInterviewScore(false);
                          }}
                          color="primary.900"
                          disabled={resumeScoreLoading}
                          p="0"
                        >
                          <X size={24} weight="bold" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => updateScore('interview')}
                          color="primary.900"
                          loading={interviewScoreLoading}
                          disabled={resumeScoreLoading}
                          p="0"
                        >
                          <Check size={24} weight="bold" />
                        </Button>
                      </Flex>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => setEditInterviewScore(true)}
                        color="primary.900"
                        p="0"
                      >
                        <PencilSimpleLine size={24} weight="bold" />
                      </Button>
                    )}
                  </Box>
                </Flex>
                <Flex justifyContent="space-between" w="100%" justifyItems="center">
                  <Box flex={1} display="flex" alignItems="center">
                    <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
                      Overall
                    </Text>
                  </Box>
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                    <Box
                      w="100px"
                      h="100px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircleProgressBar
                        value={candidate.system_score}
                        tooltipContent="System generate the overall score based on the resume and interview scores with the score algorithm."
                      />
                    </Box>
                  </Box>
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                    <Box
                      w="100px"
                      h="100px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircleProgressBar
                        value={candidate.user_score.overall_score || 0}
                        tooltipContent={
                          candidate.user_score.overall_score
                            ? 'System generate the overall score based on the resume and interview scores. If the user score is not available, the overall score will use the system score.'
                            : undefined
                        }
                      />
                    </Box>
                  </Box>
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center" />
                </Flex>
              </>
            )}

            <Text color="neutrals.700" fontSize="14px">
              Adjust the match score manually for precise evaluation. Scores range from 0 (no match)
              to 100 (perfect match).
            </Text>
            {submitError && (
              <Text color="error.700" fontSize="14px">
                Failed to update the score. Please try again.
              </Text>
            )}
            {resumeScoreError && (
              <Text color="error.700" fontSize="14px">
                {resumeScoreError}
              </Text>
            )}
            {interviewScoreError && (
              <Text color="error.700" fontSize="14px">
                {interviewScoreError}
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              w="120px"
              borderColor="primary.900"
              background="background.secondary"
              color="primary.900"
              borderRadius="8px"
              fontSize="16px"
              _hover={{
                bg: 'primary.900',
                color: 'neutrals.0'
              }}
              disabled={resumeScoreLoading || interviewScoreLoading}
            >
              Close
            </Button>
          </DialogActionTrigger>
        </DialogFooter>
        <DialogCloseTrigger m="12px">
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
